/**
 * PrivacyPolicy Component
 * STORY-LEGAL-001: Privacy Policy Page
 *
 * Displays comprehensive privacy policy covering:
 * - Data collection practices
 * - Cookie usage
 * - Third-party service disclosure (Stripe, SendGrid)
 * - User rights (GDPR, CCPA)
 */
import React, { useEffect, useRef } from 'react';
import { X, Shield, Cookie, Server, Users, Lock, Mail, Eye, Trash2 } from 'lucide-react';
import { SUPPORT_CONFIG, getMailtoLink } from '../config/support.config';
import { useLockBodyScroll } from '../hooks/useLockBodyScroll';
// BRD-PRELAUNCH-ARTIFACTS-001 PHASE_A2 A-08: import shared content module (Phase A
// retains the existing rich modal body; Phase C migrates the rich content into
// PrivacyContent.jsx and the modal becomes a thin wrapper rendering it directly).
import PrivacyContent from './legal/PrivacyContent';

export default function PrivacyPolicy({ onClose }) {
  // Prevent body scroll when modal is open
  useLockBodyScroll(true);

  const modalRef = useRef(null);
  const firstFocusableRef = useRef(null);

  // Keyboard handling for accessibility
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    firstFocusableRef.current?.focus();

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="privacy-policy-title"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal Panel */}
      <div
        ref={modalRef}
        className="relative bg-zinc-900 rounded-2xl border border-zinc-800 w-full max-w-3xl max-h-[90vh] overflow-hidden shadow-2xl flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-zinc-800 flex-shrink-0">
          <h2 id="privacy-policy-title" className="text-xl font-bold text-white flex items-center gap-2">
            <Shield className="w-5 h-5 text-orange-500" />
            Privacy Policy
          </h2>
          <button
            ref={firstFocusableRef}
            onClick={onClose}
            className="p-2 hover:bg-zinc-800 rounded-lg transition-colors"
            aria-label="Close privacy policy"
          >
            <X className="w-5 h-5 text-zinc-400" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6 space-y-6">
          {/* Last Updated */}
          <p className="text-sm text-zinc-500">
            Last Updated: {SUPPORT_CONFIG.legal.lastUpdated}
          </p>

          {/* Introduction */}
          <section>
            <p className="text-zinc-300">
              Muñoz Digital Media, LLC ("we," "our," or "us") is committed to protecting your privacy.
              This Privacy Policy explains how we collect, use, disclose, and safeguard your
              information when you visit our website or make a purchase.
            </p>
          </section>

          {/* Data Collection */}
          <section className="bg-zinc-800/50 rounded-xl p-4">
            <h3 className="font-semibold text-white mb-3 flex items-center gap-2">
              <Server className="w-4 h-4 text-orange-500" />
              Information We Collect
            </h3>
            <div className="text-sm text-zinc-400 space-y-3">
              <div>
                <h4 className="text-zinc-300 font-medium mb-1">Personal Information</h4>
                <ul className="list-disc list-inside ml-2 space-y-1">
                  <li>Name and email address (when you create an account or place an order)</li>
                  <li>Shipping and billing address</li>
                  <li>Phone number (optional, for shipping notifications)</li>
                  <li>Payment information (processed securely by Stripe)</li>
                </ul>
              </div>
              <div>
                <h4 className="text-zinc-300 font-medium mb-1">Automatically Collected Data</h4>
                <ul className="list-disc list-inside ml-2 space-y-1">
                  <li>IP address and browser type</li>
                  <li>Device information and operating system</li>
                  <li>Pages visited and time spent on site</li>
                  <li>Referring website or search terms</li>
                </ul>
              </div>
            </div>
          </section>

          {/* How We Use Data */}
          <section className="bg-zinc-800/50 rounded-xl p-4">
            <h3 className="font-semibold text-white mb-3 flex items-center gap-2">
              <Eye className="w-4 h-4 text-orange-500" />
              How We Use Your Information
            </h3>
            <ul className="text-sm text-zinc-400 list-disc list-inside ml-2 space-y-1">
              <li>Process and fulfill your orders</li>
              <li>Send order confirmations and shipping updates</li>
              <li>Respond to customer service inquiries</li>
              <li>Send promotional emails (with your consent)</li>
              <li>Improve our website and services</li>
              <li>Prevent fraud and enhance security</li>
            </ul>
          </section>

          {/* Cookies */}
          <section className="bg-zinc-800/50 rounded-xl p-4">
            <h3 className="font-semibold text-white mb-3 flex items-center gap-2">
              <Cookie className="w-4 h-4 text-orange-500" />
              Cookies & Tracking
            </h3>
            <div className="text-sm text-zinc-400 space-y-2">
              <p>We use cookies and similar technologies to:</p>
              <ul className="list-disc list-inside ml-2 space-y-1">
                <li><strong className="text-zinc-300">Essential cookies:</strong> Remember your cart and login session</li>
                <li><strong className="text-zinc-300">Analytics cookies:</strong> Understand how visitors use our site</li>
                <li><strong className="text-zinc-300">Marketing cookies:</strong> Show relevant ads (only with consent)</li>
              </ul>
              <p className="mt-3">
                You can manage cookie preferences in your browser settings. Disabling essential
                cookies may affect site functionality.
              </p>
            </div>
          </section>

          {/* Third-Party Services */}
          <section className="bg-zinc-800/50 rounded-xl p-4">
            <h3 className="font-semibold text-white mb-3 flex items-center gap-2">
              <Users className="w-4 h-4 text-orange-500" />
              Third-Party Services
            </h3>
            <div className="text-sm text-zinc-400 space-y-3">
              <p>We share data with trusted third parties to provide our services:</p>
              <div className="space-y-2">
                <div className="border-l-2 border-orange-500/50 pl-3">
                  <strong className="text-zinc-300">Stripe</strong> - Payment processing.
                  Your payment information is sent directly to Stripe and never stored on our servers.{' '}
                  <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer" className="text-orange-500 hover:text-orange-400">
                    Stripe Privacy Policy
                  </a>
                </div>
                <div className="border-l-2 border-orange-500/50 pl-3">
                  <strong className="text-zinc-300">SendGrid</strong> - Email delivery for order confirmations
                  and newsletters.{' '}
                  <a href="https://www.twilio.com/legal/privacy" target="_blank" rel="noopener noreferrer" className="text-orange-500 hover:text-orange-400">
                    SendGrid Privacy Policy
                  </a>
                </div>
                <div className="border-l-2 border-orange-500/50 pl-3">
                  <strong className="text-zinc-300">UPS</strong> - Shipping and delivery services.
                  Your shipping address is shared with UPS to fulfill orders.{' '}
                  <a href="https://www.ups.com/us/en/support/shipping-support/legal-terms-conditions/privacy-notice.page" target="_blank" rel="noopener noreferrer" className="text-orange-500 hover:text-orange-400">
                    UPS Privacy Notice
                  </a>
                </div>
              </div>
            </div>
          </section>

          {/* Data Security */}
          <section className="bg-zinc-800/50 rounded-xl p-4">
            <h3 className="font-semibold text-white mb-3 flex items-center gap-2">
              <Lock className="w-4 h-4 text-orange-500" />
              Data Security
            </h3>
            <div className="text-sm text-zinc-400 space-y-2">
              <p>We implement industry-standard security measures including:</p>
              <ul className="list-disc list-inside ml-2 space-y-1">
                <li>SSL/TLS encryption for all data transmission</li>
                <li>PCI-DSS compliant payment processing</li>
                <li>Encrypted storage for sensitive personal information</li>
                <li>Regular security audits and vulnerability assessments</li>
              </ul>
            </div>
          </section>

          {/* Your Rights */}
          <section className="bg-zinc-800/50 rounded-xl p-4">
            <h3 className="font-semibold text-white mb-3 flex items-center gap-2">
              <Trash2 className="w-4 h-4 text-orange-500" />
              Your Rights
            </h3>
            <div className="text-sm text-zinc-400 space-y-2">
              <p>Depending on your location, you may have the right to:</p>
              <ul className="list-disc list-inside ml-2 space-y-1">
                <li><strong className="text-zinc-300">Access:</strong> Request a copy of your personal data</li>
                <li><strong className="text-zinc-300">Correction:</strong> Request correction of inaccurate data</li>
                <li><strong className="text-zinc-300">Deletion:</strong> Request deletion of your personal data</li>
                <li><strong className="text-zinc-300">Portability:</strong> Request your data in a portable format</li>
                <li><strong className="text-zinc-300">Opt-out:</strong> Unsubscribe from marketing communications</li>
              </ul>
              <p className="mt-3">
                To exercise these rights, contact us at{' '}
                <a href={getMailtoLink('privacy', 'Privacy Request')} className="text-orange-500 hover:text-orange-400">
                  {SUPPORT_CONFIG.emails.privacy}
                </a>
              </p>
            </div>
          </section>

          {/* Data Retention */}
          <section className="bg-zinc-800/50 rounded-xl p-4">
            <h3 className="font-semibold text-white mb-3">Data Retention</h3>
            <p className="text-sm text-zinc-400">
              We retain your personal data for as long as necessary to fulfill the purposes outlined
              in this policy, comply with legal obligations, resolve disputes, and enforce our agreements.
              Order records are kept for 7 years for tax and legal compliance.
            </p>
          </section>

          {/* Contact */}
          <section className="text-center pt-4 border-t border-zinc-800">
            <h3 className="font-semibold text-white mb-2 flex items-center justify-center gap-2">
              <Mail className="w-4 h-4 text-orange-500" />
              Questions?
            </h3>
            <p className="text-sm text-zinc-400">
              Contact our Privacy Team at{' '}
              <a href={getMailtoLink('privacy', 'Privacy Policy Question')} className="text-orange-500 hover:text-orange-400">
                {SUPPORT_CONFIG.emails.privacy}
              </a>
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}
